import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import { 
  Search, Filter, MapPin, Phone, Mail, Globe, 
  Users, Video, Building, CheckCircle, ExternalLink,
  Stethoscope, GraduationCap, Brain, Heart, Briefcase, Baby, Tent, Upload, Image,
  HeartHandshake, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'All Categories', icon: Users },
  { value: 'therapist', label: 'Therapists', icon: Heart },
  { value: 'psychologist', label: 'Psychologists', icon: Brain },
  { value: 'doctor', label: 'Doctors & Paediatricians', icon: Stethoscope },
  { value: 'school', label: 'Schools', icon: GraduationCap },
  { value: 'early_intervention', label: 'Early Intervention', icon: Baby },
  { value: 'support_group', label: 'Support Groups', icon: Users },
  { value: 'employment', label: 'Employment Support', icon: Briefcase },
  { value: 'nanny', label: 'Nannies & Childcare', icon: Baby },
  { value: 'camp', label: 'Inclusive Camps', icon: Tent },
  { value: 'disability_charity', label: 'Disability Charities & Non-Profits', icon: HeartHandshake },
  { value: 'mental_health_charity', label: 'Mental Health Charities', icon: Sparkles }
];

const ageGroups = [
  { value: 'early_childhood', label: 'Early Childhood (0-5)' },
  { value: 'school_age', label: 'School Age (6-12)' },
  { value: 'teens', label: 'Teens (13-17)' },
  { value: 'adults', label: 'Adults (18+)' }
];

// Map category to display badge
const getCategoryBadge = (category) => {
  const cat = categories.find(c => c.value === category);
  return cat ? cat.label : category;
};

export default function Directory() {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || 'all',
    ageGroup: searchParams.get('age') || '',
    virtual: false,
    insurance: false
  });
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchServices();
  }, [filters]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== 'all') {
        params.append('category', filters.category);
      }
      if (filters.ageGroup) {
        params.append('age_group', filters.ageGroup);
      }
      if (filters.virtual) {
        params.append('virtual', 'true');
      }
      if (filters.insurance) {
        params.append('insurance', 'true');
      }

      const response = await axios.get(`${API}/services?${params.toString()}`);
      // Sort by sort_order if available, then by name
      const sortedData = response.data.sort((a, b) => {
        const orderA = a.sort_order ?? 999;
        const orderB = b.sort_order ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
      });
      setServices(sortedData);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(service => 
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogoUpload = async (serviceId) => {
    if (!selectedFile) {
      toast.error('Please select an image file');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      await axios.post(`${API}/services/${serviceId}/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Logo uploaded successfully!');
      setUploadingLogo(null);
      setSelectedFile(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  const FilterPanel = ({ mobile = false }) => (
    <div className={`space-y-6 ${mobile ? '' : 'sticky top-24'}`}>
      <div>
        <h3 className="font-semibold mb-3 text-lg">Categories</h3>
        <div className="space-y-1">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = filters.category === cat.value;
            return (
              <button
                key={cat.value}
                onClick={() => setFilters({ ...filters, category: cat.value })}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`filter-${cat.value}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-3">Age Group</h3>
        <div className="space-y-2">
          {ageGroups.map((age) => (
            <label key={age.value} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={filters.ageGroup === age.value}
                onCheckedChange={(checked) => 
                  setFilters({ ...filters, ageGroup: checked ? age.value : '' })
                }
              />
              <span className="text-sm">{age.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-3">Service Options</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={filters.virtual}
              onCheckedChange={(checked) => setFilters({ ...filters, virtual: checked })}
            />
            <span className="text-sm">Virtual Available</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={filters.insurance}
              onCheckedChange={(checked) => setFilters({ ...filters, insurance: checked })}
            />
            <span className="text-sm">Insurance Accepted</span>
          </label>
        </div>
      </div>

      <Button 
        variant="outline" 
        className="w-full"
        onClick={() => setFilters({ category: 'all', ageGroup: '', virtual: false, insurance: false })}
      >
        Clear Filters
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Header - Center Justified */}
      <div className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-12">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center">
            <Badge className="mb-4">Cayman Islands</Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Support & Services Directory</h1>
            <p className="text-muted-foreground text-lg">
              Find therapists, specialists, schools, and support services in the Cayman Islands.
            </p>
          </div>
        </div>
      </div>

      <div className="container-main py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <FilterPanel />
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search & Mobile Filter */}
            <div className="flex gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="directory-search"
                />
              </div>
              
              {/* Mobile Filter */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Filter Services</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterPanel mobile />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Results */}
            {loading ? (
              <div className="grid gap-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
                      <div className="h-4 bg-muted rounded w-full mb-2"></div>
                      <div className="h-4 bg-muted rounded w-2/3"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredServices.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Building className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Services Found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your filters or search term.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredServices.map((service) => (
                  <Card key={service.id} className="card-base" data-testid={`service-${service.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        {/* Logo/Avatar */}
                        {service.logo_url ? (
                          <img 
                            src={service.logo_url} 
                            alt={service.name}
                            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <Building className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge className="bg-[hsl(346,85%,90%)] text-[hsl(346,85%,35%)]">
                              {getCategoryBadge(service.category)}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                          <p className="text-muted-foreground mb-4">{service.description}</p>
                          
                          {/* Contact Info */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            {service.address && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="h-4 w-4" />
                                {service.address}
                              </span>
                            )}
                            {service.phone && (
                              <a href={`tel:${service.phone}`} className="flex items-center gap-1 text-primary hover:underline">
                                <Phone className="h-4 w-4" />
                                {service.phone}
                              </a>
                            )}
                            {service.email && (
                              <a href={`mailto:${service.email}`} className="flex items-center gap-1 text-primary hover:underline">
                                <Mail className="h-4 w-4" />
                                {service.email}
                              </a>
                            )}
                            {service.website && (
                              <a href={service.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                                <Globe className="h-4 w-4" />
                                Website
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                          
                          {/* Tags */}
                          <div className="flex flex-wrap gap-2 mt-4">
                            {service.virtual_available && (
                              <Badge variant="outline">
                                <Video className="h-3 w-3 mr-1" />
                                Virtual
                              </Badge>
                            )}
                            {service.in_person && (
                              <Badge variant="outline">
                                <Building className="h-3 w-3 mr-1" />
                                In-Person
                              </Badge>
                            )}
                            {service.insurance_accepted && (
                              <Badge variant="outline">Insurance Accepted</Badge>
                            )}
                            {service.age_groups?.map((age) => (
                              <Badge key={age} variant="secondary">
                                {ageGroups.find(a => a.value === age)?.label || age}
                              </Badge>
                            ))}
                            
                            {/* Admin Upload Logo Button */}
                            {isAdmin && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setUploadingLogo(service.id)}
                                className="ml-auto"
                              >
                                <Upload className="h-3 w-3 mr-1" />
                                {service.logo_url ? 'Change Logo' : 'Add Logo'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logo Upload Dialog */}
      <Dialog open={!!uploadingLogo} onOpenChange={(open) => !open && setUploadingLogo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Provider Logo</DialogTitle>
            <DialogDescription>
              Upload a logo or profile image for this service provider.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                selectedFile ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <Image className="h-10 w-10 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Click to select an image
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, GIF (Max 2MB)
                  </p>
                </div>
              )}
              <input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                accept="image/png,image/jpeg,image/gif"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                style={{ position: 'absolute', inset: 0 }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadingLogo(null); setSelectedFile(null); }}>
              Cancel
            </Button>
            <Button onClick={() => handleLogoUpload(uploadingLogo)} className="btn-primary">
              Upload Logo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
